import React, { useCallback, useEffect, useState } from 'react';
import { api, downloadFile } from '../shared/api';

type Locale = 'hr' | 'en';

type SessRow = { name: string; email: string; sessions: number; active_min: number; events: number; first_seen: string; last_seen: string; ips: number };
type UserRow = { name: string; email: string; role: string; first_login: string; last_seen: string };
type Report = { days: number; bis: SessRow[]; sandbox: SessRow[]; bis_users: UserRow[]; bis_has_history: boolean };

const fmtMin = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

function SessTable({ rows, t, empty }: { rows: SessRow[]; t: (hr: string, en: string) => string; empty: string }) {
    if (rows.length === 0) return <p className="muted" style={{ fontStyle: 'italic', padding: '6px 2px' }}>{empty}</p>;
    return (
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
            <thead><tr style={{ borderBottom: '2px solid #efe6d2', textAlign: 'left' }}>
                <th style={{ padding: '5px 8px' }}>{t('Korisnik', 'User')}</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('Sesija', 'Sessions')}</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('Aktivno', 'Active')}</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>{t('Zahtjeva', 'Requests')}</th>
                <th style={{ padding: '5px 8px' }}>{t('Prvi put', 'First')}</th>
                <th style={{ padding: '5px 8px' }}>{t('Zadnji put', 'Last')}</th>
                <th style={{ padding: '5px 8px', textAlign: 'right' }}>IP</th>
            </tr></thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.email} style={{ borderBottom: '1px solid #f3ecdb' }}>
                        <td style={{ padding: '4px 8px' }}><strong>{r.name}</strong><br /><span className="muted" style={{ fontSize: '0.72rem' }}>{r.email}</span></td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.sessions}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700 }}>{fmtMin(r.active_min)}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.events}</td>
                        <td style={{ padding: '4px 8px', fontSize: '0.76rem' }}>{r.first_seen}</td>
                        <td style={{ padding: '4px 8px', fontSize: '0.76rem' }}>{r.last_seen}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.ips}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

export function AdminActivitySection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [days, setDays] = useState(30);
    const [data, setData] = useState<Report | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [tab, setTab] = useState<'bis' | 'sandbox' | 'users'>('bis');
    const load = useCallback(async () => {
        setBusy(true); setErr('');
        try { setData(await api<Report>(`/admin/activity-report?days=${days}`, token)); }
        catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    }, [days, token]);
    useEffect(() => { void load(); }, [load]);

    return (
        <div style={{ padding: '0 4px' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>📊 {t('Aktivnost korisnika — BIS i Sandbox', 'User activity — BIS & Sandbox')}</h3>
                <label style={{ fontSize: '0.82rem' }}>{t('Zadnjih', 'Last')}:
                    <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ marginLeft: 4 }}>
                        {[7, 30, 90, 365].map((d) => <option key={d} value={d}>{d} {t('dana', 'days')}</option>)}
                    </select>
                </label>
                <div style={{ flex: 1 }} />
                <button type="button" className="secondary compact" onClick={() => void downloadFile(`/admin/activity-report/export?days=${days}`, token, `aktivnost_korisnika_${days}d.xlsx`).catch(() => alert('Export greška'))}>📥 {t('Izvoz (XLS)', 'Export (XLS)')}</button>
            </div>
            <p className="muted" style={{ fontSize: '0.78rem', marginTop: -4, marginBottom: 10 }}>
                {t('Sesija = niz zahtjeva; pauza >30 min = nova sesija. „Aktivno" = zbroj razmaka (cap 10 min/korak), bez idle-a.', 'Session = request burst; >30 min gap = new session. "Active" = sum of gaps (capped 10 min), idle excluded.')}
            </p>
            {data && !data.bis_has_history ? (
                <div style={{ background: '#fdf6e3', border: '1px solid #c99b31', borderRadius: 6, padding: '6px 10px', fontSize: '0.78rem', marginBottom: 10 }}>
                    ⚠ {t('BIS sesijsko vrijeme se broji od uvođenja audita — nema retro podataka. „BIS pristup (svi)" pokazuje povijesni prvi/zadnji login iz baze.', 'BIS session time is tracked from audit rollout — no retro data. "BIS access (all)" shows historical first/last login from the DB.')}
                </div>
            ) : null}
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                {([['bis', t('BIS sesije', 'BIS sessions')], ['sandbox', t('Sandbox sesije', 'Sandbox sessions')], ['users', t('BIS pristup (svi)', 'BIS access (all)')]] as const).map(([k, lbl]) => (
                    <button key={k} type="button" onClick={() => setTab(k)} className={tab === k ? '' : 'secondary'} style={{ fontSize: '0.8rem' }}>{lbl}{k === 'bis' ? ` (${data?.bis.length ?? 0})` : k === 'sandbox' ? ` (${data?.sandbox.length ?? 0})` : ''}</button>
                ))}
            </div>
            {err ? <p className="error">{err}</p> : null}
            {busy ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}
            {data && tab === 'bis' ? <SessTable rows={data.bis} t={t} empty={t('Nema zabilježene BIS aktivnosti u prozoru (audit tek počinje skupljati).', 'No BIS activity in window (audit just started collecting).')} /> : null}
            {data && tab === 'sandbox' ? <SessTable rows={data.sandbox} t={t} empty={t('Nema sandbox aktivnosti u prozoru.', 'No sandbox activity in window.')} /> : null}
            {data && tab === 'users' ? (
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.82rem' }}>
                    <thead><tr style={{ borderBottom: '2px solid #efe6d2', textAlign: 'left' }}>
                        <th style={{ padding: '5px 8px' }}>{t('Korisnik', 'User')}</th>
                        <th style={{ padding: '5px 8px' }}>{t('Uloga', 'Role')}</th>
                        <th style={{ padding: '5px 8px' }}>{t('Prvi login', 'First login')}</th>
                        <th style={{ padding: '5px 8px' }}>{t('Zadnje aktivan', 'Last active')}</th>
                    </tr></thead>
                    <tbody>
                        {data.bis_users.map((u) => (
                            <tr key={u.email} style={{ borderBottom: '1px solid #f3ecdb' }}>
                                <td style={{ padding: '4px 8px' }}><strong>{u.name}</strong><br /><span className="muted" style={{ fontSize: '0.72rem' }}>{u.email}</span></td>
                                <td style={{ padding: '4px 8px', fontSize: '0.76rem' }}>{u.role}</td>
                                <td style={{ padding: '4px 8px', fontSize: '0.76rem' }}>{u.first_login}</td>
                                <td style={{ padding: '4px 8px', fontSize: '0.76rem' }}>{u.last_seen}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}
        </div>
    );
}
